import {
  ArrowRight,
  CloudRain,
  MapPin,
  TrainFront,
  Zap,
  Check,
  Clock3,
} from "lucide-react";
import { ZONES, ZONE_BY_ID } from "../data/city";
import { useCity } from "../state/CityContext";
import { clock, number, StatusPill } from "./Primitives";
export function ResidentPanel() {
  const { state, dispatch, current } = useCity();
  const id = state.selected || "centre",
    place = ZONE_BY_ID[id],
    zone = current.zones.find((z) => z.id === id)!;
  const a = current.allocations[id],
    count = a.water + a.transport + a.power;
  const updates = [
    {
      system: "water" as const,
      Icon: CloudRain,
      title:
        zone.water > 50
          ? "Surface water pressure is high"
          : zone.water > 18
            ? "Some routes may be affected by water"
            : "Water conditions are settled",
      text:
        zone.water > 18
          ? "The model indicates pressure on local drainage. Allow extra time and review the affected area on the map."
          : "No significant water disruption is modelled in this area.",
    },
    {
      system: "transport" as const,
      Icon: TrainFront,
      title:
        zone.transport > 50
          ? "Travel connections are heavily disrupted"
          : zone.transport > 18
            ? "Allow extra time for your journey"
            : "Getting around looks straightforward",
      text:
        zone.transport > 18
          ? `The modelled mobility score here is ${Math.round(100 - zone.transport)}%. Pressure can come from rail disruption, water or energy.`
          : "The model shows local transport connections operating close to their baseline.",
    },
    {
      system: "power" as const,
      Icon: Zap,
      title:
        zone.power > 50
          ? "Electricity supply is under pressure"
          : zone.power > 18
            ? "Local energy demand is elevated"
            : "Energy supply is stable",
      text:
        zone.power > 18
          ? "The model suggests reduced service continuity. Power crews can ease local pressure."
          : "The model does not indicate significant local electricity supply stress.",
    },
  ];
  return (
    <section
      className="mode-panel resident-panel"
      aria-label="Resident perspective"
    >
      <span className="eyebrow panel-kicker">
        <span className="resident-dot" /> YOUR CITY / RESIDENT VIEW
      </span>
      <h1>
        What this means
        <br />
        <em>for your day.</em>
      </h1>
      <p className="panel-intro">
        The same city. The same events. A more personal perspective.
      </p>
      <label className="resident-location">
        <MapPin size={15} /> My area
        <select
          aria-label="Resident area"
          value={id}
          onChange={(e) =>
            dispatch({ type: "select", zone: e.target.value as typeof id })
          }
        >
          {ZONES.map((z) => (
            <option key={z.id} value={z.id}>
              {z.name}
            </option>
          ))}
        </select>
      </label>
      <div className="resident-summary">
        <div>
          <span className="mini-label">
            {clock(state.run.minute)} · SIMULATED UPDATE
          </span>
          <StatusPill severity={100 - zone.continuity} />
        </div>
        <h2>{place.name}</h2>
        <p>
          {zone.affected
            ? `${number(zone.affected)} fictional households are affected in this area. Changes in the city’s other systems can affect your neighbourhood too.`
            : "Your area is currently close to its everyday baseline. Explore what happens when the city’s conditions change."}
        </p>
      </div>
      <div className="resident-updates">
        {updates
          .sort((a, b) => zone[b.system] - zone[a.system])
          .map((u) => (
            <article key={u.system}>
              <u.Icon size={19} />
              <div>
                <h3>{u.title}</h3>
                <p>{u.text}</p>
                {a[u.system] > 0 && (
                  <span className="resident-action">
                    <Check size={12} />
                    {a[u.system]} {u.system === "water" ? "drainage" : u.system}{" "}
                    crew{a[u.system] > 1 ? "s" : ""} assigned
                  </span>
                )}
              </div>
            </article>
          ))}
      </div>
      <div className="resident-response">
        <Clock3 size={17} />
        <div>
          <strong>
            {count
              ? `${count} crews supporting your area`
              : "Response teams are available"}
          </strong>
          <p>
            {count
              ? "Newly assigned crews arrive in five simulated minutes and build towards full effect."
              : "Switch to the operational perspective to choose where support is needed."}
          </p>
        </div>
      </div>
      <button
        className="secondary-button"
        onClick={() => {
          dispatch({ type: "perspective", perspective: "operator" });
          dispatch({ type: "mode", mode: "respond" });
          dispatch({ type: "select", zone: id });
        }}
      >
        See how the city is responding <ArrowRight size={16} />
      </button>
      <p className="small-note">
        Experimental scenario information only. These are fictional updates, not
        public safety advice or live service information.
      </p>
    </section>
  );
}
